import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
} from 'grommet';
import { useOutletContext, useParams } from 'react-router-dom';
import { API } from '@enrich/shared-lib';
import QueryString from 'qs';

export const Students = () => {
    const user = useOutletContext();
    const { schoolSlug } = useParams();
    const school = user.school;
    const [students, setStudents] = useState([]);
    const studentTableStepOptions = [25, 50, 100];
    const [studentPage, setStudentPage] = useState(1);
    const [totalStudentItems, setTotalStudentItems] = useState(0);
    const [studentPageSize, setStudentPageSize] = useState(
        studentTableStepOptions[0],
    );

    useEffect(() => {
        if (!studentPage) return;
        let isActive = true;

        (async () => {
            const response = await loadStudents({
                schoolId: school.id,
                page: studentPage,
                pageSize: studentPageSize,
            });

            if (isActive) {
                setStudentPage(response.meta.pagination.page);
                setStudents(response.data);
                setTotalStudentItems(response.meta.pagination.total);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [school.id, studentPage, studentPageSize]);

    const handleStudentPageChange = ({ page, startIndex, endIndex }) => {
        const newPageSize = endIndex - startIndex;
        if (studentPageSize !== newPageSize) {
            setStudentPageSize(newPageSize);
            setStudentPage(1);
            return;
        }
        setStudentPage(page);
    };

    const dataColumns = [
        {
            header: 'Name',
            property: 'attributes.lastName',
            render: (contact) => {
                return (
                    <Anchor
                        key={contact.id}
                        href={`/${schoolSlug}/students/${contact.id}`}
                        label={`${contact.attributes.firstName} ${contact.attributes.lastName}`}
                    />
                );
            },
            sortable: true,
        },
        {
            header: 'Graduation',
            property: 'attributes.graduationYear',
            render: (contact) => {
                const semester = contact.attributes.graduationSemester;
                const formattedSemester = semester
                    ? semester[0].toUpperCase() + semester.slice(1)
                    : '';
                return `${formattedSemester} ${contact.attributes.graduationYear}`;
            },
        },
        {
            header: 'Hometown',
            property: 'attributes.hometown',
        },
        {
            header: 'Majors',
            property: 'attributes.majors',
            render: (contact) => {
                const majors = contact?.attributes.majors?.data?.map(
                    (major) => major.attributes.name,
                );
                return majors?.length > 0 ? majors.join(', ') : 'N/A';
            },
        },
        {
            header: 'Job Status',
            property: 'attributes.candidacies',
            render: (contact) =>
                contact.attributes.candidacies?.data?.[0]?.attributes
                    ?.status ?? '',
        },
    ];

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent gap={'medium'}>
                <Box fill="horizontal">
                    <Box
                        direction="row-responsive"
                        justify="between"
                        pad={{ right: 'small' }}>
                        <Heading level="3">Active Students</Heading>
                    </Box>
                    <Box>
                        <DataTable
                            primaryKey="id"
                            sortable
                            border="bottom"
                            data={students}
                            columns={dataColumns}
                        />
                    </Box>
                    <Box align="center" justify="center" pad="small">
                        <Pagination
                            numberItems={totalStudentItems}
                            onChange={({ page, startIndex, endIndex }) => {
                                handleStudentPageChange({
                                    page,
                                    startIndex,
                                    endIndex,
                                });
                            }}
                            step={studentPageSize}
                            stepOptions={studentTableStepOptions}
                            summary={true}
                        />
                    </Box>
                </Box>
            </PageContent>
        </Page>
    );
};

const loadStudents = async ({ schoolId, page, pageSize } = {}) => {
    const query = QueryString.stringify({
        filters: {
            enrichProgramStatus: 'active',
            school: schoolId,
        },
        populate: {
            majors: true,
            candidacies: {
                fields: ['status'],
                sort: ['updatedAt:desc'],
                pagination: { pageSize: 1 },
            },
        },
        pagination: page && pageSize ? { page, pageSize } : undefined,
        sort: ['updatedAt:desc'],
    });

    try {
        const response = await API.get(`/api/contacts?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
        return { data: [], meta: { pagination: { page: 1, total: 0 } } };
    }
};
